import { createApi } from "@reduxjs/toolkit/query/react";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import { baseQueryWithRedirect } from "features/api/utils/apiUtils.ts";
import { models } from "types/api/viewModels.ts";
import {
	GetProductLinesQueryParameters,
	GetProductLinesResponse,
	GetProductLinesResponseData
} from "types/api/globals/getProductLines.ts";
import {
	GetProductLineByIdPathParameter,
	GetProductLineByIdResponse,
	GetProductLineByIdResponseData
} from "types/api/globals/getProductLineById.ts";
import {
	GetVendorsByProductLineIdPathParameter,
	GetVendorsByProductLineIdResponseData
} from "types/api/globals/getVendorsByProductLineId.ts";

export const globalsApiSlice = createApi({
	reducerPath: "globalsApi",
	baseQuery: baseQueryWithRedirect(baseURL),
	endpoints: (builder) => ({
		getProductLines: builder.query<GetProductLinesResponseData, GetProductLinesQueryParameters>({
			query: (params) => endpoints.globals.getProductLines(params),
			transformResponse: (response: GetProductLinesResponse) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		}),
		getProductLine: builder.query<GetProductLineByIdResponseData, GetProductLineByIdPathParameter["productLineId"]>(
			{
				query: (productLineId) => endpoints.globals.getProductLine(encodeURIComponent(productLineId)),
				transformResponse: (response: GetProductLineByIdResponse) => response?.data,
				transformErrorResponse: (error: { status: string | number }) => ({
					status: error?.status
				})
			}
		),
		getPaintVendors: builder.query<
			GetVendorsByProductLineIdResponseData,
			GetVendorsByProductLineIdPathParameter["productLineId"]
		>({
			query: (productLineId) => endpoints.globals.getPaintVendors(encodeURIComponent(productLineId)),
			transformResponse: (response: models["CustomColorResponseApiResponse"]) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		}),
		getStyleData: builder.query<models["StyleViewModel"] | undefined, { productLineId: string; styleId: string }>({
			query: ({ productLineId, styleId }) =>
				endpoints.globals.getStyle(encodeURIComponent(productLineId), encodeURIComponent(styleId)),
			transformResponse: (response: models["StyleViewModelApiResponse"]) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		}),
		getSpeciesData: builder.query<
			models["SpeciesViewModel"] | undefined,
			{ productLineId: string; styleId: string; speciesId: string; customFinishes: boolean }
		>({
			query: ({ productLineId, styleId, speciesId, customFinishes }) =>
				endpoints.globals.getSpecies(
					encodeURIComponent(productLineId),
					encodeURIComponent(styleId),
					encodeURIComponent(speciesId),
					customFinishes
				),
			transformResponse: (response: models["SpeciesViewModelApiResponse"]) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		}),
		getFinishData: builder.query<
			models["FinishViewModel"] | undefined,
			{ productLineId: string; styleId: string; speciesId: string; finishId: string }
		>({
			query: ({ productLineId, styleId, speciesId, finishId }) =>
				endpoints.globals.getFinish(
					encodeURIComponent(productLineId),
					encodeURIComponent(styleId),
					encodeURIComponent(speciesId),
					encodeURIComponent(finishId)
				),
			transformResponse: (response: models["FinishViewModelApiResponse"]) => response?.data,
			transformErrorResponse: (error: { status: string | number }) => ({
				status: error?.status
			})
		})
	})
});

export const {
	useGetProductLinesQuery,
	useLazyGetProductLineQuery,
	useLazyGetStyleDataQuery,
	useGetStyleDataQuery,
	useGetSpeciesDataQuery,
	useLazyGetSpeciesDataQuery,
	useLazyGetFinishDataQuery,
	useGetFinishDataQuery,
	useGetPaintVendorsQuery
} = globalsApiSlice;
